/**
 * Objectiv Precision Floating Overlay Scrollbar
 * Provides an overlay scroll gutter and scroller on top of the viewport
 * reaching the 100% right edge without layout reservation.
 * 
 * - Idle: slides outside its clipped edge strip, without intercepting input
 * - Scrolling/right-edge proximity: 5% gutter ink and 10% thumb ink
 * - Dynamically adapts to dark and light background contrast
 */
(() => {
  const initOverlayScrollbar = () => {
    const scrollbar = document.getElementById('objScrollbar');
    const track = document.getElementById('objScrollTrack');
    const thumb = document.getElementById('objScrollThumb');
    if (!scrollbar || !thumb || !track) return;
    // Keep the sliding track and thumb together inside the fixed edge strip.
    // Also supports preview/editor markup from before the nested track revision.
    if (thumb.parentElement !== track) track.append(thumb);

    const docEl = document.documentElement;
    docEl.classList.add('obj-overlay-scroll-ready');

    let hideTimer = null;
    let isDragging = false;
    let nearEdge = false;
    const idleDelay = 2000;
    const edgeDistance = 24;
    const readScrollTop = () => window.ObjectivScroll?.y ?? window.scrollY ?? docEl.scrollTop ?? 0;
    let lastScrollTop = readScrollTop();
    let startMouseY = 0;
    let startScrollTop = 0;
    let thumbHeight = 48;
    let ticking = false;
    let scrollHeight = 0;
    let clientHeight = window.innerHeight;
    const syncEdgeCursor = () => docEl.classList.toggle('obj-scroll-edge-hover', nearEdge && scrollHeight - clientHeight > 1);

    const measureTrack = () => {
      scrollHeight = (window.ObjectivScroll?.root || docEl).scrollHeight;
      clientHeight = window.ObjectivScroll?.visibleHeight ?? window.innerHeight;
    };

    const updateThumb = () => {
      syncEdgeCursor();
      // Story copy and canvas styles change on each scroll sample. Reading the
      // document's scrollHeight here forces their entire pending layout to run.
      // Track geometry changes on resize/content changes instead.
      const maxScroll = scrollHeight - clientHeight;
      // Read the scroll position before mutating the thumb's layout.
      const scrollY = readScrollTop();

      if (maxScroll <= 1) {
        scrollbar.style.display = 'none';
        return;
      } else {
        scrollbar.style.display = '';
      }

      // Proportional thumb height (min 36px, max 80% of viewport)
      const ratio = clientHeight / scrollHeight;
      thumbHeight = Math.max(36, Math.min(clientHeight * 0.8, clientHeight * ratio));
      thumb.style.height = `${thumbHeight}px`;

      const maxThumbTop = clientHeight - thumbHeight;
      const thumbTop = Math.max(0, Math.min(maxThumbTop, (scrollY / maxScroll) * maxThumbTop));

      thumb.style.transform = `translate3d(0, ${thumbTop}px, 0)`;
    };

    const showScrollbar = () => {
      syncEdgeCursor();
      scrollbar.classList.add('is-active');
      clearTimeout(hideTimer);
      if (!isDragging && !nearEdge) {
        hideTimer = setTimeout(() => {
          scrollbar.classList.remove('is-active');
        }, idleDelay);
      }
    };

    // Scroll listener with RAF throttling
    const queueThumb = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = readScrollTop();
          updateThumb();
          if (scrollTop !== lastScrollTop) showScrollbar();
          lastScrollTop = scrollTop;
          ticking = false;
        });
        ticking = true;
      }
    };
    // This deferred script can execute before ObjectivScroll is initialized.
    // Subscribe to both signals so the phone's internal host is never missed.
    window.addEventListener('objectiv:scroll', queueThumb, { passive: true });
    window.addEventListener('scroll', queueThumb, { passive: true });

    const refreshTrack = () => { measureTrack(); updateThumb(); };
    window.addEventListener('resize', refreshTrack, { passive: true });
    window.addEventListener('objectiv:scroll-root-change', refreshTrack, { passive: true });

    // Thumb Pointer Dragging
    thumb.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      isDragging = true;
      scrollbar.classList.add('is-dragging', 'is-active');
      clearTimeout(hideTimer);
      startMouseY = e.clientY;
      startScrollTop = readScrollTop();
      try {
        thumb.setPointerCapture(e.pointerId);
      } catch (_) {}
    });

    window.addEventListener('pointermove', (e) => {
      const atEdge = e.pointerType !== 'touch' && e.clientX >= window.innerWidth - edgeDistance
        && e.clientX <= window.innerWidth && e.clientY >= 0 && e.clientY <= window.innerHeight;
      if (atEdge !== nearEdge) {
        nearEdge = atEdge;
        showScrollbar();
      }
      if (!isDragging) return;
      e.preventDefault();
      const maxThumbTop = clientHeight - thumbHeight;
      if (maxThumbTop <= 0) return;

      const deltaY = e.clientY - startMouseY;
      const maxScroll = scrollHeight - clientHeight;
      const scrollDelta = (deltaY / maxThumbTop) * maxScroll;

      (window.ObjectivScroll || window).scrollTo(0, startScrollTop + scrollDelta);
    });

    const stopDrag = (e) => {
      if (!isDragging) return;
      isDragging = false;
      scrollbar.classList.remove('is-dragging');
      if (e && e.pointerId !== undefined) {
        try {
          thumb.releasePointerCapture(e.pointerId);
        } catch (_) {}
      }
      showScrollbar();
    };

    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);

    // Track click to jump
    track.addEventListener('pointerdown', (e) => {
      if (e.target === thumb || e.button !== 0) return;
      e.preventDefault();
      const clickY = e.clientY;
      const maxThumbTop = clientHeight - thumbHeight;
      if (maxThumbTop <= 0) return;

      const targetThumbTop = Math.max(0, Math.min(maxThumbTop, clickY - thumbHeight / 2));
      const maxScroll = scrollHeight - clientHeight;
      const targetScroll = (targetThumbTop / maxThumbTop) * maxScroll;

      (window.ObjectivScroll || window).scrollTo({ top: targetScroll,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      showScrollbar();
    });

    const leaveEdge = () => {
      if (!nearEdge) return;
      nearEdge = false;
      showScrollbar();
    };
    docEl.addEventListener('pointerleave', leaveEdge);
    window.addEventListener('blur', event => { leaveEdge(); stopDrag(event); });

    // ResizeObserver to detect document height mutations
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        refreshTrack();
      });
      resizeObserver.observe(docEl);
      if (document.body) resizeObserver.observe(document.body);
      const content = document.querySelector('[data-objectiv-scroll-content]');
      if (content) resizeObserver.observe(content);
    }

    // Initial setup
    refreshTrack();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOverlayScrollbar);
  } else {
    initOverlayScrollbar();
  }
})();
